import { jwtDecode } from 'jwt-decode';
import { customAlphabet } from 'nanoid/non-secure';

export class InvalidTokenException extends Error {
  message = 'InvalidTokenException';
}

export class RefreshTokenException extends Error {
  message = 'RefreshTokenException';
}

export interface JsonWebToken {
  uid: string;
  exp: string;
}

const generate = customAlphabet('0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz', 20);

export const generateRequestId = () => {
  return `hcreq:${generate()}`;
};

/**
 * Name of JWT access token
 */
export const ACCESS_TOKEN_NAME = 'accessToken';

/**
 * Name of JWT subsctiption token. Subscription token is used for GraphQL subscriptions.
 */
export const SUBSCRIPTION_TOKEN_NAME = 'subscriptionToken';

/**
 * Name of JWT refresh token
 */
export const REFRESH_TOKEN_NAME = 'refreshToken';

/**
 * Default refresh token URL
 */
// @ts-expect-error - this is a hack to get the value from Vite environment
const DEFAULT_REFRESH_TOKEN_URL = typeof __REFRESH_TOKEN_URL__ !== 'undefined' ? __REFRESH_TOKEN_URL__ : '/auth/refresh-token';

/**
 * Retrieves token from localStorage
 *
 */
export const storageGet = (key: string) => {
  return localStorage.getItem(key) || null;
};

/**
 * Saves token to storage
 *
 */
export const storageSet = (key: string, value: string) => {
  localStorage.setItem(key, value);
};

/**
 * Removes token from storage
 *
 * @param {string} item
 */
export const storageRemove = (item: string) => {
  localStorage.removeItem(item);
};

/**
 * Remove all tokens from storage
 * @deprecated Use `removaAllTokensFromStorage` instead
 */
export const cleanStorage = () => {
  removeAllTokensFromStorage();
};

/**
 * Remove all tokens from storage
 */
export const removeAllTokensFromStorage = () => {
  storageRemove(ACCESS_TOKEN_NAME);
  storageRemove(SUBSCRIPTION_TOKEN_NAME);
  storageRemove(REFRESH_TOKEN_NAME);
};

/**
 * Saves access token to storage
 */
export const setAccessToken = (value: string) => {
  storageSet(ACCESS_TOKEN_NAME, value);
};

/**
 * Saves subscription token to storage
 */
export const setSubscriptionToken = (value: string) => {
  storageSet(SUBSCRIPTION_TOKEN_NAME, value);
};

/**
 * Saves refresh token to storage
 */
export const setRefreshToken = (value: string) => {
  storageSet(REFRESH_TOKEN_NAME, value);
};

/**
 * Retrieves an access token from a storage
 */
export const getAccessToken = () => {
  return storageGet(ACCESS_TOKEN_NAME);
};

/**
 * Retrieves a subscription token from a storage
 */
export const getSubscriptionToken = () => {
  return storageGet(SUBSCRIPTION_TOKEN_NAME);
};

/**
 * Retrieves an refresh token from a storage
 *
 * @returns {string | null}
 */
export const getRefreshToken = () => {
  return storageGet(REFRESH_TOKEN_NAME);
};

/**
 * Returns token expiration date from JWT header
 *
 * @link https://jwt.io/
 * @returns {*}
 */
export const getExpiryDate = () => {
  const token = getAccessTokenData();

  // If we cannot determine expiration date,
  // assume it as some default date in the past
  if (!token || !token.exp) {
    return new Date('2000-01-01');
  }

  return new Date(+token.exp * 1000);
};

/**
 * Create fetch request to refresh token
 */
export const createRefreshTokenRequest = async (url: string = DEFAULT_REFRESH_TOKEN_URL) => {
  const refreshToken = storageGet(REFRESH_TOKEN_NAME);

  if (!refreshToken) {
    // No refresh token, remove all tokens and throw
    removeAllTokensFromStorage();
    throw new RefreshTokenException('No refresh token available.');
  }

  return await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-request-id': generateRequestId(),
    },
    body: JSON.stringify({
      refreshToken: getRefreshToken(),
    }),
  });
};

/**
 * Returns true if token is expired
 *
 */
export const isAccessTokenExpired = () => {
  // If token's TTL is in the past or null
  const expiryDate = getExpiryDate();

  return !expiryDate || new Date().getTime() >= expiryDate.getTime();
};

/**
 * Returns true if token exists
 */
export const hasAccessToken = () => {
  return Boolean(getAccessToken());
};

/**
 * Returns decoded access token
 *
 * @returns {JsonWebToken}
 */
export const getAccessTokenData = () => {
  const encodedToken = getAccessToken();

  if (!encodedToken) {
    return null;
  }

  const token: JsonWebToken = jwtDecode(encodedToken);

  return token;
};

/**
 * Checks if token is set and not expired
 *
 */
export const verifyAccessToken = () => {
  const token = getAccessToken();

  if (!token || isAccessTokenExpired()) {
    throw new InvalidTokenException();
  }
};

/**
 * Regenerates a new access token and refresh token via API and stores in a storage
 */
export const refreshTokens = async (refreshTokenUrl: string = DEFAULT_REFRESH_TOKEN_URL): Promise<void> => {
  const refreshToken = storageGet(REFRESH_TOKEN_NAME);
  if (!refreshToken) {
    // No refresh token, clear `accessToken` too and throw
    removeAllTokensFromStorage();
    throw new RefreshTokenException('No refresh token available.');
  }

  const response = await createRefreshTokenRequest(refreshTokenUrl);
  const {
    accessToken,
    refreshToken: newRefreshToken,
    subscriptionToken,
  } = await response.json() as {
    accessToken: string;
    refreshToken: string;
    subscriptionToken: string;
  };

  if (!accessToken) {
    removeAllTokensFromStorage();
    throw new RefreshTokenException('Access token not found in refresh token response json.');
  }

  if (!newRefreshToken) {
    removeAllTokensFromStorage();
    throw new RefreshTokenException('Refresh token not found in refresh token response json.');
  }

  setAccessToken(accessToken);
  setRefreshToken(newRefreshToken);

  if (subscriptionToken) {
    setSubscriptionToken(subscriptionToken);
  }
};
